import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Building2, Users, PoundSterling, FileText, CheckCircle, Clock,
  ArrowLeft, TrendingUp, Calendar, Activity, Home, AlertTriangle,
  ChevronRight, BarChart3
} from "lucide-react";
import { API } from "@/App";

const SolicitorFirmDashboard = () => {
  const navigate = useNavigate();
  const { firmId } = useParams();
  const [loading, setLoading] = useState(true);
  const [firmData, setFirmData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFirmData();
  }, [firmId]);

  const fetchFirmData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/solicitors/firm/${encodeURIComponent(firmId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFirmData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load firm data");
      toast.error("Failed to load solicitor firm data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'bg-green-100 text-green-700',
      'exchanged': 'bg-blue-100 text-blue-700',
      'completed': 'bg-emerald-100 text-emerald-700',
      'fallen_through': 'bg-red-100 text-red-700',
      'pending': 'bg-amber-100 text-amber-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Error Loading Data</h2>
            <p className="text-slate-500 mb-4">{error}</p>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { firm, overview, cases, monthly_stats, recent_activity, outstanding_tasks } = firmData || {};

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/sales/progression')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Sales
              </Button>
              <div className="h-8 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-800">{firm?.name}</h1>
                  <p className="text-sm text-slate-500">{firm?.contact_email || 'Solicitor Firm'}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate(`/sales/solicitor/${encodeURIComponent(firmId)}/analytics`)}
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                View Analytics
              </Button>
              <Badge className="bg-indigo-100 text-indigo-700 text-sm px-3 py-1">
                {overview?.total_cases || 0} Active Files
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <FileText className="w-8 h-8 opacity-80" />
                <span className="text-3xl font-bold">{overview?.total_cases || 0}</span>
              </div>
              <p className="text-indigo-100 font-medium">Active Files</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <PoundSterling className="w-8 h-8 opacity-80" />
                <span className="text-2xl font-bold">{formatCurrency(overview?.total_pipeline)}</span>
              </div>
              <p className="text-emerald-100 font-medium">Pipeline Value</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-8 h-8 opacity-80" />
                <span className="text-3xl font-bold">{overview?.tasks_completed || 0}</span>
              </div>
              <p className="text-blue-100 font-medium">Tasks Completed</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Clock className="w-8 h-8 opacity-80" />
                <span className="text-3xl font-bold">{overview?.tasks_outstanding || 0}</span>
              </div>
              <p className="text-amber-100 font-medium">Outstanding</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Monthly Exchange & Completion Stats */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-600" />
                Exchanges & Completions by Month
              </CardTitle>
              <CardDescription>Performance over the last 6 months</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {monthly_stats?.length > 0 ? (
                  monthly_stats.map((month, idx) => (
                    <div key={idx} className="flex items-center gap-4">
                      <div className="w-24 text-sm font-medium text-slate-600">{month.month}</div>
                      <div className="flex-1 flex gap-2">
                        <div className="flex-1 bg-slate-100 rounded-full h-8 relative overflow-hidden">
                          <div 
                            className="absolute left-0 top-0 h-full bg-blue-500 rounded-full flex items-center justify-end pr-2"
                            style={{ width: `${Math.min((month.exchanges / 5) * 100, 100)}%` }}
                          >
                            {month.exchanges > 0 && (
                              <span className="text-xs font-bold text-white">{month.exchanges}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex-1 bg-slate-100 rounded-full h-8 relative overflow-hidden">
                          <div 
                            className="absolute left-0 top-0 h-full bg-emerald-500 rounded-full flex items-center justify-end pr-2"
                            style={{ width: `${Math.min((month.completions / 5) * 100, 100)}%` }}
                          >
                            {month.completions > 0 && (
                              <span className="text-xs font-bold text-white">{month.completions}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 text-center py-8">No monthly data available</p>
                )}
                <div className="flex justify-center gap-6 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-blue-500 rounded"></div>
                    <span className="text-sm text-slate-600">Exchanges</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-emerald-500 rounded"></div>
                    <span className="text-sm text-slate-600">Completions</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-indigo-600" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {recent_activity?.length > 0 ? (
                  recent_activity.map((activity, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className={`w-2 h-2 rounded-full mt-2 ${
                        activity.type === 'completion' ? 'bg-emerald-500' :
                        activity.type === 'exchange' ? 'bg-blue-500' :
                        activity.type === 'milestone' ? 'bg-amber-500' : 'bg-slate-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{activity.description}</p>
                        <p className="text-xs text-slate-500">{activity.property}</p>
                        <p className="text-xs text-slate-400 mt-1">{activity.date}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 text-center py-4">No recent activity</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Active Cases Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Home className="w-5 h-5 text-indigo-600" />
              Active Cases with {firm?.name}
            </CardTitle>
            <CardDescription>{cases?.length || 0} files currently in progress</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">Client</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">Property</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">Price</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">Stage</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">Status</th>
                    <th className="text-right py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {cases?.map((c, idx) => (
                    <tr key={idx} className="border-b hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-800">{c.buyer_name || c.seller_name || 'Unknown'}</p>
                        <p className="text-xs text-slate-400">{c.buyer_name ? 'Buyer' : 'Seller'}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-slate-700">{c.address}</p>
                        <p className="text-sm text-slate-500">{c.postcode}</p>
                      </td>
                      <td className="py-3 px-4 font-semibold text-slate-800">
                        {formatCurrency(c.price)}
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-slate-600">{c.stage || 'In Progress'}</span>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={getStatusColor(c.status)}>
                          {c.status?.replace(/_/g, ' ').toUpperCase() || 'ACTIVE'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/sales/case/${c.case_id}/hub`)}
                        >
                          View <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!cases || cases.length === 0) && (
                <p className="text-center text-slate-500 py-8">No active cases with this firm</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Outstanding Tasks */}
        {outstanding_tasks?.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="w-5 h-5" />
                Outstanding Items
              </CardTitle>
              <CardDescription className="text-amber-700">
                {outstanding_tasks.length} items requiring attention
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {outstanding_tasks.map((task, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                    <div>
                      <p className="font-medium text-slate-800">{task.description}</p>
                      <p className="text-sm text-slate-500">{task.property}</p>
                    </div>
                    <Badge className={task.overdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                      {task.overdue ? 'Overdue' : 'Pending'}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default SolicitorFirmDashboard;
